<template>
    <div class="excel-uploader">
        <div class="mb-3 d-flex justify-content-start">
            <button @click="exportarPlantilla" class="btn btn-success">
                <i class="fas fa-file-download mr-2"></i>Descargar plantilla Excel
            </button>
        </div>

        <DropFileBasic ref="DropFileBasic" @handleFileData="handleFileUpload" @change="handleFileUpload" :fileTypes="[
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        ]" />
        <div v-if="excelData.length > 0" class="preview-table mt-4">
            <h3 class="h4 mb-3">Vista Previa de Datos</h3>
            <div class="table-responsive">
                <table class="table table-bordered table-striped">
                    <thead>
                        <tr>
                            <th v-for="(header, index) in headers" :key="index">{{ header }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(row, rowIndex) in excelData" :key="rowIndex">
                            <td v-for="(value, colIndex) in row" :key="colIndex">{{ value }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div class="d-flex justify-content-between mt-3">
                <button @click="limpiarDatosExcel()" class="btn btn-secondary">
                    <i class="fas fa-times mr-2"></i>Cancelar
                </button>
                <button @click="importarDatos()" class="btn btn-primary">
                    <i class="fas fa-file-import mr-2"></i>Importar Puntos
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import * as XLSX from "xlsx";
import DropFileBasic from "@/components/helexium/DropFileBasic.vue";

export default {
    name: 'ExcelUploader',
    components: {
        DropFileBasic
    },
    props: {
        // Plantillas disponibles para buscar por ID
        plantillasDisponibles: {
            type: Array,
            required: true
        },
        // Plantillas ya seleccionadas (para evitar duplicados)
        plantillasSeleccionadas: {
            type: Array,
            default: () => []
        },
        // Puntos existentes (para evitar duplicados)
        puntosExistentes: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            excelData: [],
            headers: [],
            // Nombres exactos de las columnas esperadas
            columnasEsperadas: {
                idPlantilla: "ID_PLANTILLA",
                numeroIdentificacion: "NUMERO_IDENTIFICACION",
                descripcion: "DESCRIPCION",
                fechaHora: "FECHA_HORA_ENTREGA"
            },
            // Nueva columna para buscar por nombre de cliente
            columnaBusqueda: "BUSCAR_POR_NOMBRE_CLIENTE"
        };
    },
    methods: {
        exportarPlantilla() {
            try {
                // Crear una hoja de cálculo con las columnas esperadas y la nueva columna de búsqueda
                const worksheetMain = XLSX.utils.aoa_to_sheet([
                    [
                        this.columnaBusqueda,
                        this.columnasEsperadas.idPlantilla,
                        this.columnasEsperadas.numeroIdentificacion,
                        this.columnasEsperadas.descripcion,
                        this.columnasEsperadas.fechaHora
                    ]
                ]);

                // Añadir una fila con la fórmula
                XLSX.utils.sheet_add_aoa(worksheetMain, [
                    [
                        "", // Celda para ingresar el nombre del cliente
                        {
                            t: 'f', // Tipo fórmula
                            f: 'IF(LEN(A2)>0,INDEX(\'Plantillas Disponibles\'!A:A,MATCH(A2,\'Plantillas Disponibles\'!B:B,0)),"")'
                        },
                        "",
                        "",
                        ""
                    ]
                ], { origin: "A2" });

                // Configurar el ancho de las columnas
                const wscols = [
                    { wch: 30 }, // Ancho para BUSCAR_POR_NOMBRE_CLIENTE
                    { wch: 15 }, // Ancho para ID_PLANTILLA
                    { wch: 25 }, // Ancho para NUMERO_IDENTIFICACION
                    { wch: 30 }, // Ancho para DESCRIPCION
                    { wch: 20 }  // Ancho para FECHA_HORA_ENTREGA
                ];
                worksheetMain['!cols'] = wscols;

                // Crear un libro de trabajo y añadir la hoja principal
                const workbook = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(workbook, worksheetMain, "Plantilla");

                // Si hay plantillas disponibles, crear una hoja separada para ellas
                if (this.plantillasDisponibles.length > 0) {
                    // Crear una nueva hoja para las plantillas disponibles
                    const worksheetPlantillas = XLSX.utils.aoa_to_sheet([
                        ["ID_PLANTILLA", "LUGAR_ENTREGA", "Latitud", "Longitud", "Zona", "Nombre_Cliente", "Direccion_Punto_Entrega", "CodigoSN", "Persona_Que_Recibe", "Contacto_Persona_Que_Recibe"]
                    ]);

                    // Añadir los datos de las plantillas
                    const plantillasResumen = this.plantillasDisponibles.map(p => [
                        p.id,
                        p.lugarEntrega,
                        p.latUbicacionEntrega,
                        p.longUbicacionEntrega,
                        p.ubicacion,
                        p.nombreCliente,
                        p.direccionPuntoEntrega,
                        p.codigoSN,
                        p.nombrePersonaCliente,
                        p.contactoPersonaCliente
                    ]);

                    XLSX.utils.sheet_add_aoa(worksheetPlantillas, plantillasResumen, { origin: "A2" });

                    // Configurar el ancho de las columnas para la hoja de plantillas
                    const plantillasCols = [
                        { wch: 15 }, // ID_PLANTILLA
                        { wch: 25 }, // LUGAR_ENTREGA
                        { wch: 15 }, // Latitud
                        { wch: 15 }, // Longitud
                        { wch: 15 }, // Zona
                        { wch: 30 }, // Nombre_Cliente
                        { wch: 30 }, // Direccion_Punto_Entrega
                        { wch: 15 }, // CodigoSN
                        { wch: 25 }, // Persona_Que_Recibe
                        { wch: 25 }  // Contacto_Persona_Que_Recibe
                    ];
                    worksheetPlantillas['!cols'] = plantillasCols;

                    // Añadir esta hoja al libro de trabajo
                    XLSX.utils.book_append_sheet(workbook, worksheetPlantillas, "Plantillas Disponibles");

                    // Añadir una hoja de ayuda con instrucciones
                    const worksheetAyuda = XLSX.utils.aoa_to_sheet([
                        ["INSTRUCCIONES PARA USAR LA PLANTILLA"],
                        [""],
                        ["1. En la hoja 'Plantilla', escriba el nombre del cliente en la columna 'BUSCAR_POR_NOMBRE_CLIENTE'"],
                        ["2. El ID de la plantilla se completará automáticamente si el nombre del cliente existe en la hoja 'Plantillas Disponibles'"],
                        ["3. Complete los demás campos según sea necesario"],
                        ["4. Si la fórmula no funciona, asegúrese de que el nombre del cliente esté escrito exactamente igual que en la hoja 'Plantillas Disponibles'"],
                        [""],
                        ["Nota: La búsqueda distingue entre mayúsculas y minúsculas"]
                    ]);

                    // Configurar el ancho de las columnas para la hoja de ayuda
                    worksheetAyuda['!cols'] = [{ wch: 100 }];

                    // Añadir esta hoja al libro de trabajo
                    XLSX.utils.book_append_sheet(workbook, worksheetAyuda, "Instrucciones");
                }

                // Guardar el archivo
                XLSX.writeFile(workbook, "plantilla_puntos_entrega.xlsx");

                this.$emit('mensaje', {
                    tipo: 'success',
                    titulo: 'Plantilla exportada',
                    mensaje: 'La plantilla de Excel se ha descargado correctamente'
                });
            } catch (error) {
                console.error("Error al exportar la plantilla:", error);
                this.$emit('mensaje', {
                    tipo: 'error',
                    titulo: 'Error',
                    mensaje: 'No se pudo exportar la plantilla de Excel'
                });
            }
        },

        handleFileUpload(event) {
            const file = event;
            if (!file) return;

            const reader = new FileReader();
            reader.onload = (e) => {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: "array" });

                // Tomar la primera hoja
                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];

                // Convertir a JSON
                const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

                // Extraer encabezados y datos
                this.headers = jsonData[0] || [];
                this.excelData = jsonData.slice(1);
            };

            reader.readAsArrayBuffer(file);
        },

        limpiarDatosExcel() {
            this.excelData = [];
            this.headers = [];
            this.$emit('limpiar');
            this.$refs.DropFileBasic.restaurar();
        },

        importarDatos() {
            if (this.excelData.length === 0) {
                this.$emit('mensaje', {
                    tipo: 'warning',
                    titulo: 'Sin datos',
                    mensaje: 'No hay datos para importar'
                });
                return;
            }

            // Mapear los índices de las columnas necesarias
            const headerMap = {
                idPlantilla: this.headers.findIndex(h => h === this.columnasEsperadas.idPlantilla),
                numeroIdentificacion: this.headers.findIndex(h => h === this.columnasEsperadas.numeroIdentificacion),
                descripcion: this.headers.findIndex(h => h === this.columnasEsperadas.descripcion),
                fechaHora: this.headers.findIndex(h => h === this.columnasEsperadas.fechaHora)
            };

            // Verificar que exista la columna de ID de plantilla
            if (headerMap.idPlantilla === -1) {
                this.$emit('mensaje', {
                    tipo: 'error',
                    titulo: 'Formato incorrecto',
                    mensaje: `El archivo no contiene la columna "${this.columnasEsperadas.idPlantilla}"`
                });
                return;
            }

            // Extraer los datos del Excel
            const puntosExcel = this.excelData
                .filter(row => row.length > 0 && row[headerMap.idPlantilla]) // Filtrar filas vacías o sin ID
                .map(row => {
                    // Convertir el ID a string para comparación
                    const idPlantilla = String(row[headerMap.idPlantilla]).trim();

                    // Datos adicionales (si existen las columnas)
                    const numeroIdentificacion = headerMap.numeroIdentificacion !== -1 ? row[headerMap.numeroIdentificacion] : '';
                    const descripcion = headerMap.descripcion !== -1 ? row[headerMap.descripcion] : '';
                    const fechaHora = headerMap.fechaHora !== -1 ? row[headerMap.fechaHora] : '';

                    return {
                        idPlantilla,
                        numeroIdentificacion,
                        descripcion,
                        fechaHora
                    };
                });

            if (puntosExcel.length === 0) {
                this.$emit('mensaje', {
                    tipo: 'warning',
                    titulo: 'Sin datos válidos',
                    mensaje: 'No se encontraron filas con datos válidos en el archivo'
                });
                return;
            }

            // Buscar las plantillas correspondientes a los IDs
            const plantillasEncontradas = [];
            const plantillasNoEncontradas = [];
            const datosAdicionales = {};

            puntosExcel.forEach(punto => {
                // Buscar la plantilla por ID
                const plantilla = this.plantillasDisponibles.find(p => String(p.id) === punto.idPlantilla);

                if (plantilla) {
                    // Verificar si ya está seleccionada o existe en los puntos
                    const yaSeleccionada = this.plantillasSeleccionadas.some(p => p.id === plantilla.id);
                    const yaExiste = this.puntosExistentes.some(p => p.plantillaRutasId === plantilla.id);

                    if (!yaSeleccionada && !yaExiste) {
                        plantillasEncontradas.push(plantilla);

                        // Guardar los datos adicionales asociados a esta plantilla
                        datosAdicionales[plantilla.id] = {
                            numeroDocumentoEntrega: punto.numeroIdentificacion,
                            descripcionPuntoEntrega: punto.descripcion,
                            fechaHoraEntrega: punto.fechaHora
                        };
                    }
                } else {
                    plantillasNoEncontradas.push(punto.idPlantilla);
                }
            });

            // Emitir resultados
            this.$emit('importar', {
                plantillasEncontradas,
                plantillasNoEncontradas,
                datosAdicionales,
                total: puntosExcel.length
            });

            // Limpiar datos después de importar
            this.limpiarDatosExcel();
        }
    }
};
</script>
<style>
.text-right,
.text-danger,
.pointer mb-2 {
    display: none;
}
</style>
<style scoped>
.excel-uploader {
    border: 1px solid #ddd;
    padding: 15px;
    border-radius: 5px;
    text-align: center;
}
</style>